import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { TIER } from '../utils/tier-constants';
import { SubscriptionEditorFields, SubscriptionFormData } from '../components/SubscriptionEditorFields';
import { SubscriptionSummary } from '../components/SubscriptionSummary';
import { validateFilters, getCouplingUpdates, ValidationIssue, prepareSubscriptionFilters } from '../utils/subscriptionValidation';
import { PageHeader, Banner, Button } from '../components/ui';
import './SubscriptionForm.css';
import { useCreateSubscription, useUpdateSubscription, useSubscriptions, useSubscriptionFromList } from '../hooks/queries/useSubscriptions';
import { useHealth } from '../hooks/queries/useHealth';
import { useGeoData } from '../contexts/GeoDataContext';
import { buildAreaLabel } from '../utils/areaLabel';

const SubscriptionForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditing = !!id;

  const [formData, setFormData] = useState<SubscriptionFormData>({
    zipCodes: [],
    notificationChannels: ['telegram'],
    enabled: true,
    uiMetadata: { zones: [] },
    filters: {
      minRent: '',
      maxRent: '',
      minBaseRent: '',
      maxBaseRent: '',
      includeUnknowns: true,
      minRooms: '',
      maxRooms: '',
      minSpace: '',
      maxSpace: '',
      wbsFilter: 'ANY',
      wgFilter: 'ANY',
      swapFilter: 'ANY',
      tempFilter: 'ANY',
      providerFilter: 'ANY',
      furnishedFilter: 'ANY',
      closedAreaFilter: 'NONE',
    },
  });

  const [error, setError] = useState('');
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [isLoading, setIsLoading] = useState(isEditing);
  const [isBaseRentActive, setIsBaseRentActive] = useState(false);
  const [hasZoneConstraint, setHasZoneConstraint] = useState(false);
  const [formTouched, setFormTouched] = useState(false);

  // React Query Hooks
  // Use shared list cache to avoid stale state issues (same fix as Admin Modal)
  const subscription = useSubscriptionFromList(id!, user?.userId || '');
  const { data: mySubscriptions = [] } = useSubscriptions(user?.userId);

  const createMutation = useCreateSubscription();
  const updateMutation = useUpdateSubscription();

  // Compute zip codes from OTHER subscriptions (not the one being edited)
  const otherSubscriptionZips = useMemo(() => {
    const zips = new Set<string>();
    for (const sub of mySubscriptions) {
      if (isEditing && sub.subscriptionId === id) continue;
      if (sub.zipCodes) {
        for (const z of sub.zipCodes) zips.add(z);
      }
    }
    return zips;
  }, [mySubscriptions, id, isEditing]);

  // Initialize form when subscription loads (for editing)
  useEffect(() => {
    if (subscription) {
      if (!formTouched) {
        // No user edits yet — fully re-initialize from server data
        loadSubscriptionData(subscription);
      } else {
        // User has pending edits — only update originalSubscription baseline
        // and merge server-side notificationChannels (set by Telegram linking)
        setOriginalSubscription(subscription);
        setFormData(prev => ({
          ...prev,
          notificationChannels: subscription.notificationChannels || [],
        }));
      }
    }
    // formTouched is read intentionally but excluded from deps: including it here
    // makes the effect re-run the instant a user edit sets formTouched to true,
    // which immediately overwrites the just-changed notificationChannels with the
    // stale server value (see issue I4). Only `subscription` changing (e.g. a
    // refetch after Telegram linking) should re-trigger this merge.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription]);

/* Removed manual useEffect for loadSubscription */

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        navigate('/dashboard', { state: { noRedirect: true } });
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [navigate]);

  const loadSubscriptionData = (sub: any) => {
      const hasBaseRent = !!(sub.filters?.minBaseRent || sub.filters?.maxBaseRent);
      setIsBaseRentActive(hasBaseRent);

      const subData: SubscriptionFormData = {
        zipCodes: sub.zipCodes || [],
        uiMetadata: sub.uiMetadata || { zones: [] },
        notificationChannels: sub.notificationChannels || [], // Use the raw channels
        enabled: sub.enabled ?? true,
        filters: {
          minRent: sub.filters?.minRent?.toString() || '',
          maxRent: sub.filters?.maxRent?.toString() || '',
          minBaseRent: sub.filters?.minBaseRent?.toString() || '',
          maxBaseRent: sub.filters?.maxBaseRent?.toString() || '',
          includeUnknowns: sub.filters?.includeUnknowns ?? true,
          minRooms: sub.filters?.minRooms?.toString() || '',
          maxRooms: sub.filters?.maxRooms?.toString() || '',
          minSpace: sub.filters?.minSpace?.toString() || '',
          maxSpace: sub.filters?.maxSpace?.toString() || '',
          wbsFilter: sub.filters?.wbsFilter || 'ANY',
          wgFilter: sub.filters?.wgFilter || 'ANY',
          swapFilter: sub.filters?.swapFilter || 'ANY',
          tempFilter: sub.filters?.tempFilter || 'ANY',
          providerFilter: sub.filters?.providerFilter || 'ANY',
          furnishedFilter: sub.filters?.furnishedFilter || 'ANY',
          closedAreaFilter: sub.filters?.closedAreaFilter || 'NONE',
        },
      };

      setFormData(subData);
      setOriginalSubscription(sub);
      setIsLoading(false);
  };
/* Removed loadSubscription async function */

  const [originalSubscription, setOriginalSubscription] = useState<any>(null);
  /* Removed manual fetching of allSubscriptions */


  const handleFieldChange = (field: string | Partial<SubscriptionFormData>, value?: any) => {
    setFormTouched(true);
    if (typeof field === 'object') {
      setFormData(prev => ({ ...prev, ...field }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFormTouched(true);
    setFormData(prev => {
      const updates = getCouplingUpdates(field, value, prev.filters);
      const newFilters = { ...prev.filters, ...updates };

      // Perform validation immediately
      const validationIssues = validateFilters(newFilters, { isBaseRentActive });
      setIssues(validationIssues);

      return { ...prev, filters: newFilters };
    });
  };

  const handleBaseRentToggle = (active: boolean) => {
    setIsBaseRentActive(active);
    // Re-validate when toggled
    const validationIssues = validateFilters(formData.filters, { isBaseRentActive: active });
    setIssues(validationIssues);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Telegram Migration Logic
    if (originalSubscription &&
        originalSubscription.notificationChannels?.includes('telegram') &&
        !formData.notificationChannels.includes('telegram')) {

      const telegramTarget = originalSubscription.notificationTargets?.telegram;
      const chatId = telegramTarget?.chatId || originalSubscription.chatId;
      const chatType = telegramTarget?.chatType;
      const isGroup = chatType === 'group' || chatType === 'supergroup';
      const isLinked = !!chatId;

      if (isLinked) {
        if (isGroup) {
          if (!window.confirm('Achtung: Wenn du den Benachrichtigungskanal änderst, verlässt der Bot diese Telegram-Gruppe. Fortfahren?')) {
            return;
          }
        } else if (chatType === 'private' && chatId === user?.telegramId) {
          // Direct chat safeguard - only for the main private chat
          if (mySubscriptions.length > 1) {
            setError('Bitte lösche zuerst alle anderen Suchen, bevor du den Benachrichtigungskanal dieser Suche änderst.');
            return;
          }

          const newPref = formData.notificationChannels[0]?.toUpperCase() || 'NONE';
          if (!window.confirm('Möchtest du den Benachrichtigungskanal wirklich von Telegram auf ' + newPref + ' umstellen?')) {
            return;
          }
        }
      }
    }

    // Final validation
    const validationIssues = validateFilters(formData.filters, { isBaseRentActive });
    if (validationIssues.some(i => i.type === 'error')) {
      setError('Bitte behebe die markierten Fehler.');
      return;
    }

    if (!formData.zipCodes || formData.zipCodes.length === 0) {
      setError('Mindestens eine PLZ ist erforderlich');
      return;
    }

    const invalidZip = formData.zipCodes.find(zip => !/^\d{5}$/.test(zip));
    if (invalidZip) {
      setError(`Ungültige PLZ: ${invalidZip}. Muss 5-stellig sein`);
      return;
    }

    if (user?.tier === TIER.FREE && formData.zipCodes.length > 15) {
      setError('Free-Nutzer können maximal 15 PLZ wählen (3 Kernzone + 12 Randzone)');
      return;
    }

    setIsLoading(true);

    try {
      const payload: any = {
        userId: user?.userId,
        zipCodes: formData.zipCodes,
        notificationChannels: formData.notificationChannels,
        enabled: formData.enabled,
        uiMetadata: formData.uiMetadata,
        filters: prepareSubscriptionFilters(formData.filters, { isBaseRentActive })
      };

      // -- CHANGE DETECTION --
      if (isEditing && originalSubscription) {
        const isDirty = (
            JSON.stringify(payload.zipCodes) !== JSON.stringify(originalSubscription.zipCodes) ||
            JSON.stringify(payload.notificationChannels) !== JSON.stringify(originalSubscription.notificationChannels) ||
            payload.enabled !== originalSubscription.enabled ||
            JSON.stringify(payload.filters) !== JSON.stringify(originalSubscription.filters) ||
            JSON.stringify(payload.uiMetadata) !== JSON.stringify(originalSubscription.uiMetadata)
        );

        if (!isDirty) {
            console.log("No changes detected, skipping PUT");
            navigate('/dashboard', { state: { noRedirect: true } });
            return;
        }

        // Only send notificationChannels if user explicitly changed it.
        // Prevents overwriting server-side Telegram links added after form load.
        if (JSON.stringify(payload.notificationChannels) === JSON.stringify(originalSubscription.notificationChannels)) {
            delete payload.notificationChannels;
        }
      }

      if (isEditing) {
        await updateMutation.mutateAsync({ id: id!, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }

      navigate('/dashboard', {
        state: { noRedirect: true, toast: isEditing ? 'Änderungen gespeichert' : 'Suche angelegt' },
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Speichern');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsLoading(false);
    }
  };

  const { data: health } = useHealth();
  // Zone definitions are already loaded by the editor's place search; null until then.
  const { zoneDefinitions } = useGeoData();
  const isMaintenance = health?.maintenanceMode && user?.tier !== TIER.ADMIN;

  const zipCount = formData.zipCodes?.length || 0;
  const summaryMaxRent = formData.filters.maxRent ? parseInt(formData.filters.maxRent, 10) : null;
  const summaryMinRooms = formData.filters.minRooms ? parseFloat(formData.filters.minRooms) : null;
  const summaryMinArea = formData.filters.minSpace ? parseInt(formData.filters.minSpace, 10) : null;

  const isSubmitDisabled = isLoading || issues.some(i => i.type === 'error') || hasZoneConstraint || isMaintenance;
  const isSaving = isLoading || createMutation.isPending || updateMutation.isPending;

  return (
    <div className="mx-auto flex max-w-narrow flex-col pb-28 lg:max-w-[56rem]">
      <PageHeader
        title={isEditing ? 'Suche bearbeiten' : 'Neue Suche'}
        description="Wo und was du suchst. Filter kannst du jederzeit ändern."
      />

      <form onSubmit={handleSubmit} className="subscription-form mt-6">
        {error && (
          <Banner tone="danger" className="mb-4">{error}</Banner>
        )}

        {isMaintenance && (
          <Banner tone="warning" className="mb-4" title="Wartungsmodus">
            Das System wird gerade aktualisiert. Änderungen sind momentan nicht möglich.
          </Banner>
        )}

        <SubscriptionEditorFields
          formData={formData}
          userTier={user?.tier || TIER.FREE}
          isAdmin={!!user?.isAdmin}
          isLoading={isLoading}
          onFieldChange={handleFieldChange}
          onFilterChange={handleFilterChange}
          issues={issues}
          isBaseRentActive={isBaseRentActive}
          onBaseRentToggle={handleBaseRentToggle}
          onConstraintChange={setHasZoneConstraint}
          otherSubscriptionZips={otherSubscriptionZips}
          variant="page"
        />

        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-card/95 pb-[env(safe-area-inset-bottom)] backdrop-blur">
          <div className="mx-auto flex max-w-narrow flex-wrap items-center justify-between gap-3 px-4 py-3 lg:max-w-[56rem]">
            <SubscriptionSummary
              zipCount={zipCount}
              areaLabel={buildAreaLabel(formData.zipCodes || [], zoneDefinitions)}
              maxRent={summaryMaxRent}
              minRooms={summaryMinRooms}
              minArea={summaryMinArea}
              channels={formData.notificationChannels}
              filters={formData.filters}
            />
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate('/dashboard', { state: { noRedirect: true } })}
                disabled={isLoading}
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                className="min-w-[12rem]"
                loading={isSaving}
                disabled={isSubmitDisabled}
                title={isMaintenance ? 'System befindet sich im Wartungsmodus. Änderungen sind momentan nicht möglich.' : undefined}
              >
                {isMaintenance ? 'Wartungsmodus' : isEditing ? 'Änderungen speichern' : 'Suche anlegen'}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SubscriptionForm;
